import groq from 'groq'
import { type SanityClient } from 'next-sanity'
import type {
  
  // Main Content Types
  Page,
  Category,
  Timber,
  Inspiration,
  System,
  SystemTimber,
  About,  
  
  // Config
  SiteConfig,
} from './sanity.interfaces'

// Common fragments
const pageModulesFragment = groq`
  pageModules[]-> {
    _id,
    _type,
    title,
    layout,
    body,
    videoFiles,
    imageCarousel,
    fileCollection[] {
      ...,
      "fileURL": asset->url,
    },
    contentType,
    imageOne,
    imageTwo,
    previewImage,
    logo,
    imageGrid,
  }
`

// Base queries
export const pagesQuery = groq`*[_type == "page" && defined(slug.current)] | order(_createdAt desc)`
export const categoriesQuery = groq`*[_type == "category" && defined(slug.current)] | order(order asc)`
export const timbersQuery = groq`*[_type == "timber" && defined(slug.current)] | order(title asc)`
export const inspirationsQuery = groq`*[_type == "inspiration" && defined(slug.current)] | order(order asc)`
export const systemsQuery = groq`*[_type == "system" && defined(slug.current)] | order(order asc)`
export const systemsQueryType = groq`*[_type == "system" && defined(slug.current) && systemType in $systemTypes] | order(order asc)`
export const systemTimbersQuery = groq`*[_type == "systemTimber" && defined(slug.current)] | order(order asc)`
export const aboutsQuery = groq`*[_type == "about" && defined(slug.current)] | order(_createdAt desc)`

// Detailed queries using the fragment
export const pageBySlugQuery = groq`*[_type == "page" && slug.current == $slug]{
  ...,
  ${pageModulesFragment}
}[0]`

export const categoryBySlugQuery = groq`*[_type == "category" && slug.current == $slug]{
  ...,
  isLink,
  linkUrl,
  ${pageModulesFragment},
  selectedTimbers[]-> {
    ...,
  },
  selectedProducts[]-> {
    ...,
  },
  selectedSystems[]-> {
    ...,
  },
}[0]`

export const systemBySlugQuery = groq`*[_type == "system" && slug.current == $slug]{
  ...,
  ${pageModulesFragment},
  selectedSystemTimbers[]-> {
    ...,
  },
  selectedTimbers[]-> {
    ...,
  },
  selectedSystems[]-> {
    ...,
  },
}[0]`

// Specialized queries
export const timberBySlugQuery = groq`*[_type == "timber" && slug.current == $slug] {
  ...,
  selectedTimbers[]-> {
    ...,
  },
  selectedSystems[]-> {
    ...,
  },
  "factSheetURL": factSheet.asset->url,
  "brochureSheetURL": brochureSheet.asset->url,
  "specSheetURL": specSheet.asset->url,
}[0]`

export const inspirationBySlugQuery = groq`*[_type == "inspiration" && slug.current == $slug]{
  ...,
  selectedTimbers[]-> {
    ...,
  },
  selectedInspirations[]-> {
    ...,
  },
  selectedProducts[]-> {
    ...,
  },
  selectedSystems[]-> {
    ...,
  },
}[0]`

export const systemTimberBySlugQuery = groq`*[_type == "systemTimber" && slug.current == $slug]{
  ...,
  sections[] {
    ...,
    tabs[] {
      ...,
      profiles[] {
        ...,
        profileFiles[] {
          ...,
          "dwgFileURL": dwgFile.asset->url,
          "dwgFileName": dwgFile.asset->originalFilename,
          "pdfFileURL": pdfFile.asset->url,
          "pdfFileName": pdfFile.asset->originalFilename,
          "rvtFleURL": rvtFle.asset->url,
          "rvtFileName": rvtFle.asset->originalFilename,
        }
      }
    }
  }
}[0]`

export const aboutBySlugQuery = groq`*[_type == "about" && slug.current == $slug][0]`

// Slug queries using the fragment
export const pageSlugsQuery = groq`
*[_type == "page" && defined(slug.current)]{
  ...,
  ${pageModulesFragment}
}[].slug.current
`

export const categorySlugsQuery = groq`
*[_type == "category" && defined(slug.current)]{
  ...,
  ${pageModulesFragment}
}[].slug.current
`

export const systemSlugsQuery = groq`
*[_type == "system" && defined(slug.current)]{
  ...,
  ${pageModulesFragment}
}[].slug.current
`

export const timberSlugsQuery = groq`
*[_type == "timber" && defined(slug.current)][].slug.current
`

export const inspirationSlugsQuery = groq`
*[_type == "inspiration" && defined(slug.current)][].slug.current
`

export const systemTimberSlugsQuery = groq`
*[_type == "systemTimber" && defined(slug.current)][].slug.current
`

export const aboutSlugsQuery = groq`
*[_type == "about" && defined(slug.current)][].slug.current
`

// Site config query
export const siteConfigQuery = groq`
*[_type == "siteConfig"]{
  ...,
  mainNav-> {
    title,   
    items[] {
      _key,
      text,
      navigationItemUrl {
        internalLink-> {
          title,
          slug
        },
        externalUrl
      }
    },
  },
  secondaryNav-> {
    title,   
    items[] {
      _key,
      text,
      navigationItemUrl {
        internalLink-> {
          title,
          slug
        },
        externalUrl
      }
    },
  },
  footerNav-> {
    title,   
    items[] {
      _key,
      text,
      navigationItemUrl {
        internalLink-> {
          title,
          slug
        },
        externalUrl
      }
    },
  }
}[0]`

// Fetch functions
export async function getPages(client: SanityClient): Promise<Page[]> {
  return await client.fetch(pagesQuery)
}

export async function getCategories(client: SanityClient): Promise<Category[]> {
  return await client.fetch(categoriesQuery)
}

export async function getTimbers(client: SanityClient): Promise<Timber[]> {
  return await client.fetch(timbersQuery)
}

export async function getInspirations(client: SanityClient): Promise<Inspiration[]> {
  return await client.fetch(inspirationsQuery)
}

export async function getSystems(client: SanityClient): Promise<System[]> {
  return await client.fetch(systemsQuery)
}

export async function getSystemsByType(client: SanityClient, systemTypes: Array<String>): Promise<System[]> {
  return await client.fetch(systemsQueryType, { systemTypes })
}

export async function getSystemTimbers(client: SanityClient): Promise<SystemTimber[]> {
  return await client.fetch(systemTimbersQuery)
}

export async function getAbouts(client: SanityClient): Promise<About[]> {
  return await client.fetch(aboutsQuery)
}

export async function getPage(
  client: SanityClient,
  slug: string,
): Promise<Page> {
  return await client.fetch(pageBySlugQuery, { slug })
}

export async function getCategory(
  client: SanityClient,
  slug: string,
): Promise<Category> {
  return await client.fetch(categoryBySlugQuery, { slug })
}

export async function getTimber(
  client: SanityClient,
  slug: string,
): Promise<Timber> {
  return await client.fetch(timberBySlugQuery, { slug })
}

export async function getInspiration(
  client: SanityClient,
  slug: string,
): Promise<Inspiration> {
  return await client.fetch(inspirationBySlugQuery, { slug })
}

export async function getSystem(
  client: SanityClient,
  slug: string,
): Promise<System> {
  return await client.fetch(systemBySlugQuery, { slug })
}

export async function getSystemTimber(
  client: SanityClient,
  slug: string,
): Promise<SystemTimber> {
  return await client.fetch(systemTimberBySlugQuery, { slug })
}

export async function getAbout(
  client: SanityClient,
  slug: string,
): Promise<About> {
  return await client.fetch(aboutBySlugQuery, { slug })
}

export async function getSiteConfig(
  client: SanityClient,
): Promise<SiteConfig> {
  return await client.fetch(siteConfigQuery)
}
